/**
 * PilotLeague - Gaming Theme
 * 
 * Style: Fun, coloré, emojis, animations
 * Cible: Gamers, simmers 18-35 ans
 * Inspiration: Gaming platforms, Twitch, Discord
 */

export const gamingTheme = {
  name: 'gaming',
  label: 'Gaming',
  
  // ============================================================================
  // ÉLÉMENTS COMMUNS (partagés dark/light)
  // ============================================================================
  common: {
    // Rayons de bordure (Gaming: rounded-xl pour cartes)
    radius: {
      sm: 'rounded-lg',
      md: 'rounded-xl',
      lg: 'rounded-2xl',
      full: 'rounded-full',
    },
    // Transitions (Gaming: animées)
    transition: {
      fast: 'transition-all duration-150',
      normal: 'transition-all duration-300',
      slow: 'transition-all duration-500',
    },
    // Ombres (Gaming: glow amber)
    shadow: {
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      glow: 'shadow-lg shadow-amber-500/20',
    },
    // Typographie Gaming: Bold, fun
    typography: {
      heading: 'font-bold',       // font-weight: 700
      body: 'font-normal',        // font-weight: 400
      label: 'font-semibold',     // font-weight: 600
      tracking: 'tracking-wide',  // letter-spacing
      uppercase: false,           // Labels en minuscules
    },
  },

  // ============================================================================
  // MODE SOMBRE
  // ============================================================================
  dark: {
    name: 'dark',
    
    // Backgrounds
    bg: {
      primary: 'bg-slate-950',
      secondary: 'bg-slate-900',
      tertiary: 'bg-slate-800',
      card: 'bg-slate-900/60',
      cardHover: 'hover:bg-slate-800/80',
      cardAccent: 'bg-gradient-to-br from-amber-900/20 via-slate-900 to-slate-900',
      input: 'bg-slate-950',
      nav: 'bg-slate-900/95 backdrop-blur-xl',
      modal: 'bg-slate-900',
      overlay: 'bg-black/60',
    },
    
    // Textes
    text: {
      primary: 'text-white',
      secondary: 'text-slate-200',
      tertiary: 'text-slate-400',
      muted: 'text-slate-500',
      inverse: 'text-slate-950',
      accent: 'text-amber-400',
      success: 'text-emerald-400',
      warning: 'text-amber-400',
      danger: 'text-red-400',
      info: 'text-sky-400',
    },
    
    // Bordures
    border: {
      primary: 'border-slate-700',
      secondary: 'border-slate-800',
      accent: 'border-amber-500/30',
      accentStrong: 'border-amber-500/60',
      success: 'border-emerald-500/30',
      danger: 'border-red-500/30',
    },
    
    // Accents & Gradients
    accent: {
      primary: 'bg-amber-500',
      primaryHover: 'hover:bg-amber-400',
      primaryText: 'text-amber-400',
      gradient: 'bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500',
      gradientText: 'bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent',
      subtle: 'bg-amber-500/10',
    },
    
    // États
    states: {
      success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
      warning: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
      danger: 'bg-red-500/10 border-red-500/30 text-red-400',
      info: 'bg-sky-500/10 border-sky-500/30 text-sky-400',
    },
    
    // Scores (couleurs selon valeur)
    score: {
      excellent: 'text-emerald-400', // >= 85
      good: 'text-sky-400',          // >= 70
      average: 'text-amber-400',     // >= 50
      poor: 'text-red-400',          // < 50
    },
    
    // Couleurs HEX pour jauges SVG (selon charte graphique)
    scoreHex: {
      excellent: '#10b981', // emerald-500 >= 85
      good: '#3b82f6',      // blue-500 >= 70
      average: '#f59e0b',   // amber-500 >= 50
      poor: '#ef4444',      // red-500 < 50
    },
    
    // Badges de rareté (Gaming style - Tier 1-4)
    rarity: {
      legendary: 'bg-amber-500/20 border-amber-500/50 text-amber-300', // ⭐ Legendary
      epic: 'bg-purple-500/20 border-purple-500/50 text-purple-300',    // 💎 Epic
      rare: 'bg-sky-500/20 border-sky-500/50 text-sky-300',             // 💠 Rare
      common: 'bg-slate-800 border-slate-600 text-slate-300',           // Common
    },
  },

  // ============================================================================
  // MODE CLAIR
  // ============================================================================
  light: {
    name: 'light',
    
    // Backgrounds
    bg: {
      primary: 'bg-slate-50',
      secondary: 'bg-white',
      tertiary: 'bg-slate-100',
      card: 'bg-white',
      cardHover: 'hover:bg-slate-50',
      cardAccent: 'bg-gradient-to-br from-amber-50 via-white to-white',
      input: 'bg-white',
      nav: 'bg-white/95 backdrop-blur-xl',
      modal: 'bg-white',
      overlay: 'bg-black/40',
    },
    
    // Textes
    text: {
      primary: 'text-slate-900',
      secondary: 'text-slate-700',
      tertiary: 'text-slate-500',
      muted: 'text-slate-400',
      inverse: 'text-white',
      accent: 'text-amber-600',
      success: 'text-emerald-600',
      warning: 'text-amber-600',
      danger: 'text-red-600',
      info: 'text-sky-600',
    },
    
    // Bordures
    border: {
      primary: 'border-slate-200',
      secondary: 'border-slate-100',
      accent: 'border-amber-300',
      accentStrong: 'border-amber-400',
      success: 'border-emerald-300',
      danger: 'border-red-300',
    },
    
    // Accents & Gradients
    accent: {
      primary: 'bg-amber-500',
      primaryHover: 'hover:bg-amber-600',
      primaryText: 'text-amber-600',
      gradient: 'bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500',
      gradientText: 'bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent',
      subtle: 'bg-amber-50',
    },
    
    // États
    states: {
      success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      warning: 'bg-amber-50 border-amber-200 text-amber-700',
      danger: 'bg-red-50 border-red-200 text-red-700',
      info: 'bg-sky-50 border-sky-200 text-sky-700',
    },
    
    // Scores
    score: {
      excellent: 'text-emerald-600',
      good: 'text-sky-600',
      average: 'text-amber-600',
      poor: 'text-red-600',
    },
    
    // Couleurs HEX pour jauges SVG
    scoreHex: {
      excellent: '#059669', // emerald-600 >= 85
      good: '#2563eb',      // blue-600 >= 70
      average: '#d97706',   // amber-600 >= 50
      poor: '#dc2626',      // red-600 < 50
    },
    
    // Badges de rareté (Gaming style - Tier 1-4)
    rarity: {
      legendary: 'bg-amber-100 border-amber-300 text-amber-700',  // ⭐ Legendary
      epic: 'bg-purple-100 border-purple-300 text-purple-700',     // 💎 Epic
      rare: 'bg-sky-100 border-sky-300 text-sky-700',              // 💠 Rare
      common: 'bg-slate-100 border-slate-300 text-slate-600',      // Common
    },
  },

  // ============================================================================
  // CARACTÉRISTIQUES DU STYLE
  // ============================================================================
  features: {
    // Utiliser des emojis
    useEmojis: true,
    
    // Style de langage
    language: 'fun',
    
    // Labels et textes
    labels: {
      score: 'Score',
      xp: 'XP',
      level: 'Level',
      rank: 'Rank',
      landing: {
        excellent: '🧈 Butter!',
        good: '👍 Nice!',
        average: '😐 Okay',
        poor: '💥 Ouch!',
      },
      achievements: {
        legendary: '⭐ Legendary',
        epic: '💎 Epic',
        rare: '💠 Rare',
        common: 'Common',
      },
      greetings: {
        morning: '🌅 Good morning',
        afternoon: '☀️ Good afternoon',
        evening: '🌙 Good evening',
      },
    },
    
    // Animations
    animations: {
      enabled: true,
      scoreReveal: true,
      xpGain: true,
      levelUp: true,
    },
    
    // Affichage XP
    showXP: true,
    showStreak: true,
    
    // Icônes spéciales
    icons: {
      flight: '✈️',
      landing: '🛬',
      score: '🎯',
      trophy: '🏆',
      medal: '🥇',
      fire: '🔥',
      star: '⭐',
      crown: '👑',
    },
  },
};

export default gamingTheme;
